"use client";

import { useId } from "react";

import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  type TooltipMode,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export function SettingsFieldLabel({
  text,
  required = false,
  description,
  descriptionMode = "hint",
}: {
  text: React.ReactNode;
  required?: boolean;
  description?: React.ReactNode;

  descriptionMode?: TooltipMode;
}) {
  const hasDescription =
    description != null &&
    (typeof description !== "string" || description.trim() !== "");

  return (
    <span>
      {hasDescription ? (
        <Tooltip mode={descriptionMode}>
          <TooltipTrigger
            render={
              <span className="cursor-help border-b border-dotted border-foreground/50">
                {text}
              </span>
            }
          />
          <TooltipContent className="max-w-xs whitespace-pre-wrap text-left leading-relaxed">
            {description}
          </TooltipContent>
        </Tooltip>
      ) : (
        text
      )}
      {required ? <span className="ml-0.5 text-destructive">*</span> : null}
    </span>
  );
}

export type SettingsRowColumns = 1 | 2 | 3 | 4;

const SETTINGS_ROW_GRID_TEMPLATES: Record<SettingsRowColumns, string> = {
  1: "[--settings-label-w:fit-content(10em)] sm:grid-cols-[var(--settings-label-w)_minmax(0,1fr)]",
  2: "[--settings-label-w:6em] sm:grid-cols-[var(--settings-label-w)_minmax(0,1fr)_var(--settings-label-w)_minmax(0,1fr)]",
  3: "[--settings-label-w:6em] sm:grid-cols-[var(--settings-label-w)_minmax(0,1fr)_var(--settings-label-w)_minmax(0,1fr)_var(--settings-label-w)_minmax(0,1fr)]",
  4: "[--settings-label-w:6em] sm:grid-cols-[var(--settings-label-w)_minmax(0,1fr)_var(--settings-label-w)_minmax(0,1fr)_var(--settings-label-w)_minmax(0,1fr)_var(--settings-label-w)_minmax(0,1fr)]",
};

const LABEL_CHAR_REM = 0.875;

const LABEL_REQUIRED_MARK_REM = 0.75;

export function settingsLabelWidthStyle(
  labelChars?: number,
): React.CSSProperties | undefined {
  if (!labelChars || labelChars <= 0) {
    return undefined;
  }

  const rem = labelChars * LABEL_CHAR_REM + LABEL_REQUIRED_MARK_REM;

  return {
    "--settings-label-w": `minmax(${rem}rem, max-content)`,
  } as React.CSSProperties;
}

const SETTINGS_ROW_GRID_FIRST_ROW: Record<SettingsRowColumns, string> = {
  1: "",
  2: "sm:[&:not(:has(>[data-full-width]:nth-child(-n+2)))>*:nth-child(-n+2)]:border-t-0",
  3: "sm:[&:not(:has(>[data-full-width]:nth-child(-n+3)))>*:nth-child(-n+3)]:border-t-0",
  4: "sm:[&:not(:has(>[data-full-width]:nth-child(-n+4)))>*:nth-child(-n+4)]:border-t-0",
};

export function settingsRowGridClass(columns: SettingsRowColumns = 1) {
  return cn(
    "grid grid-cols-1",
    SETTINGS_ROW_GRID_TEMPLATES[columns],
    columns > 1 && "sm:items-start sm:gap-x-4",
    SETTINGS_ROW_GRID_FIRST_ROW[columns],
  );
}

export const settingsRowGroupClass = settingsRowGridClass(1);

export const settingsRowTwoColGroupClass = settingsRowGridClass(2);

export const settingsRowFourCharLabelGroupClass =
  "grid grid-cols-1 sm:grid-cols-[6em_minmax(0,1fr)]";

export function SettingsRowGrid({
  columns = 1,
  labelChars,
  className,
  children,
}: {
  columns?: SettingsRowColumns;

  labelChars?: number;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(settingsRowGridClass(columns), className)}
      style={settingsLabelWidthStyle(labelChars)}
    >
      {children}
    </div>
  );
}

export function SettingsSection({
  legend,
  action,
  children,
  className,
  bodyClassName,
  columns = 1,
  labelChars,
}: {
  legend: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;

  bodyClassName?: string;

  columns?: SettingsRowColumns;

  labelChars?: number;
}) {
  const legendId = useId();

  return (
    <section
      role="group"
      aria-labelledby={legendId}
      className={cn(
        "overflow-hidden rounded-lg border border-border/70",
        className,
      )}
    >
      <div
        className={cn(
          "border-b border-border/60 bg-muted/40 px-3 py-2",
          action ? "flex items-center justify-between gap-3" : "",
        )}
      >
        <div id={legendId} className="text-sm font-medium text-foreground">
          {legend}
        </div>
        {action}
      </div>
      <div
        className={
          bodyClassName ?? cn(settingsRowGridClass(columns), "px-3")
        }
        style={bodyClassName ? undefined : settingsLabelWidthStyle(labelChars)}
      >
        {children}
      </div>
    </section>
  );
}

export const settingsConfigKvAddButtonClass =
  "text-xs text-primary transition-colors hover:text-primary/80 disabled:pointer-events-none disabled:opacity-50";

export const settingsCompactControlsClass =
  "[&_[data-slot=number-field-group]]:h-7 [&_input]:h-7 [&_input]:text-xs [&_button:not([data-slot^=number-field-])]:h-7 [&_button]:text-xs [&_[role=combobox]]:h-7 [&_[role=combobox]]:text-xs";

export function SettingsFieldPanel({
  title,
  titleExtra,
  actions,
  className,
  children,
}: {

  title?: React.ReactNode;

  titleExtra?: React.ReactNode;

  actions?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        "overflow-hidden rounded-md border border-border/70",
        "text-xs",
        settingsCompactControlsClass,
        className,
      )}
    >
      {/* 内容为空（如「无数据就不渲染表格」）时不画下边框，免得框里留一条孤零零的线 */}
      <div
        className={cn(
          "flex min-h-9 flex-wrap items-center justify-between gap-3 bg-muted/40 px-3 py-1 [&_label]:text-xs [&_span]:text-xs",
          children == null || children === false
            ? undefined
            : "border-b border-border/60",
        )}
      >
        {/* 无标题时这一格是空的，justify-between 仍把操作区推到右端 */}
        <div className="flex items-center gap-3">
          {title == null ? null : (
            <Label className="text-xs text-foreground/90">{title}</Label>
          )}
          {titleExtra}
        </div>
        {actions}
      </div>
      {children}
    </div>
  );
}

export function SettingsConfigKvBlock({
  label,
  description,
  descriptionMode = "hint",
  required = false,
  variant = "split",
  contentClassName,
  action = null,
  children,
}: {
  label: React.ReactNode;

  description?: React.ReactNode;

  descriptionMode?: TooltipMode;

  required?: boolean;

  variant?: "split" | "panel";

  contentClassName?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  const labelNode =
    typeof label === "string" ? (
      <SettingsFieldLabel
        text={label}
        required={required}
        description={description}
        descriptionMode={descriptionMode}
      />
    ) : (
      label
    );

  if (variant === "panel") {
    return (
      <div
        data-full-width
        className="border-t border-border/40 py-2.5 first:border-t-0 sm:col-span-full"
      >
        <SettingsFieldPanel title={labelNode} actions={action}>
          {/* 边框归面板画，压掉首个子元素的外框，免得「框里套框」；后续子元素不动 */}
          <div
            className={
              contentClassName ??
              "[&>:first-child]:rounded-none [&>:first-child]:border-0"
            }
          >
            {children}
          </div>
        </SettingsFieldPanel>
      </div>
    );
  }

  return (
    <>
      <div
        className={cn(
          "border-t border-border/40 pt-2.5 pb-1 first:border-t-0",
          "sm:col-span-2 sm:grid sm:grid-cols-subgrid sm:items-center sm:gap-x-4",
        )}
      >
        <Label className="mb-1.5 text-foreground/90 sm:col-start-1 sm:mb-0 sm:justify-end sm:text-right">
          {labelNode}
        </Label>
        <div className="flex min-h-8 items-center justify-end sm:col-start-2">
          {action}
        </div>
      </div>
      <div className="col-span-full pb-3.5">{children}</div>
    </>
  );
}

export function SettingsRow({
  label,
  htmlFor,
  description,
  descriptionMode = "hint",
  required = false,
  error,
  children,
  align = "top",
  fullWidth = false,
  hideTopBorder = false,
}: {
  label: React.ReactNode;
  htmlFor?: string;

  description?: React.ReactNode;

  descriptionMode?: TooltipMode;

  required?: boolean;

  error?: string | null;
  children: React.ReactNode;

  align?: "center" | "top";

  fullWidth?: boolean;

  hideTopBorder?: boolean;
}) {
  const centerControls = align === "center";
  const hasError = Boolean(error);
  const labelNode =
    typeof label === "string" ? (
      <SettingsFieldLabel
        text={label}
        required={required}
        description={description}
        descriptionMode={descriptionMode}
      />
    ) : (
      label
    );

  return (
    <div
      data-slot="settings-row"
      data-full-width={fullWidth || undefined}
      className={cn(

        "border-t border-border/40 py-2.5 first:border-t-0",
        hideTopBorder && "sm:border-t-0",
        fullWidth ? "sm:col-span-full" : "sm:col-span-2",
        "sm:grid sm:grid-cols-subgrid sm:gap-x-4",

        centerControls ? "sm:items-center" : "sm:items-start",
      )}
    >
      <Label
        htmlFor={htmlFor}
        className={cn(
          "mb-1.5 text-foreground/90 sm:col-start-1 sm:mb-0 sm:justify-end sm:text-right",
          "sm:min-h-8 sm:items-center",
        )}
      >
        {labelNode}
      </Label>
      <div
        className={cn(
          "flex min-w-0 flex-col sm:col-start-2 sm:w-full",

          fullWidth && "sm:[grid-column-end:-1]",
          hasError && "space-y-1.5",
        )}
      >
        {/* 外层顶对齐后这格高度即控件高度，居中只对不足 32px 的矮控件生效 */}
        <div className="flex min-h-8 w-full items-center">
          {/* 开关是行内元素，留在文字基线上会被下缘空隙顶高约 2px，改成块级才与标签同心 */}
          <div className="min-w-0 w-full [&>[data-slot=switch]]:flex">
            {children}
          </div>
        </div>
        {hasError ? (
          <p className="text-xs leading-relaxed text-destructive">{error}</p>
        ) : null}
      </div>
    </div>
  );
}
